import httpService from '@/modules/common/services/httpService';

const gatewayService = httpService.create(window.pov_config.VUE_APP_API_URL_GATEWAY);
const channelsBasePath = 'business/messaging/v1/channels/';
const baseRoute = 'v3/communication';

const getCallsSettings = async () => gatewayService.get(`${baseRoute}/voice_call_settings`).then((res) => res.data);

const createCallsSettings = async (settings) => gatewayService.post(`${baseRoute}/voice_call_settings`, settings).then((res) => res.data);

const updateCallsSettings = async (settingsUid, updatedSettings) => gatewayService.put(`${baseRoute}/voice_call_settings/${settingsUid}`, updatedSettings).then((res) => res.data);

const getRemainingQuota = async () => gatewayService.get(`${baseRoute}/voice_call_quotas`).then((res) => res.data);

const getBusinessActiveDedicatedNumber = async (params) => {
  // Use the new v3 API to get business phone numbers
  const result = await gatewayService.get(`${baseRoute}/business_phone_numbers`, {
    features: JSON.stringify(['VOICE', 'SMS']),
    ...params,
  });
  const numbers = result.data?.data?.business_phone_numbers || [];
  // Find the active dedicated number (not from pool)
  return numbers.find((number) => number.phone_number && !number.pool);
};

const claimNumber = async ({ voiceTextAppInstalled }) => httpService.post(
  `${channelsBasePath}`,
  { new_api: true, channel: { provider: 'nexmo', voice_text_app_installed: voiceTextAppInstalled } },
).then((res) => res.data?.data.channel);

const getAvailablePhoneNumbers = async ({ areaCode, countryCode, page, perPage, features = '["VOICE"]' }) => gatewayService
  .get(`${baseRoute}/available_phone_numbers`, {
    country_code: countryCode,
    area_code: areaCode,
    features,
    page,
    per_page: perPage,
  })
  .then((res) => res.data?.data);

const createBusinessPhoneNumber = async({ phoneNumber, countryCode, features = ['VOICE'] }) => gatewayService
  .post(`${baseRoute}/business_phone_numbers`, {
    phone_number: phoneNumber,
    country_code: countryCode,
    features,
  });

const getBusinessPhoneNumbers = async () => gatewayService
  .get(`${baseRoute}/business_phone_numbers`)
  .then((res) => res.data?.data);

/**
 * Get available phone numbers with VOICE and SMS features
 * @param {string} countryCode - The country code (default: 'US')
 * @returns {Promise<Array>} - Array of available phone numbers
 */
const getAvailableVoiceSmsNumbers = async (countryCode = 'US') => gatewayService
  .get(`${baseRoute}/available_phone_numbers`, {
    country_code: countryCode,
    features: JSON.stringify(['VOICE', 'SMS']),
  })
  .then((res) => res.data?.data?.available_phone_numbers || []);

/**
 * Claim a phone number with verification request data for Voice & Texting
 * @param {Object} params - The claim parameters
 * @param {string} params.phoneNumber - The phone number to claim
 * @param {string} params.countryCode - The country code (default: 'US')
 * @param {boolean} params.enableTexting - Whether texting is enabled
 * @param {Object|null} params.verificationRequestData - The verification data (required if texting enabled)
 * @returns {Promise<Object>} - The claimed phone number data
 */
const claimPhoneNumberWithVerification = async ({
  phoneNumber,
  countryCode = 'US',
  enableTexting = false,
  verificationRequestData = null,
}) => {
  const payload = {
    phone_number: phoneNumber,
    country_code: countryCode,
    features: ['VOICE', 'SMS'],
  };

  if (enableTexting) {
    // If texting is enabled, set status to ACTIVE and include verification data
    payload.status = 'ACTIVE';
    if (verificationRequestData) {
      payload.verification_request_data = verificationRequestData;
    }
  } else {
    // If texting is disabled, set status to SMS_INACTIVE
    payload.status = 'SMS_INACTIVE';
  }

  const response = await gatewayService.post(`${baseRoute}/business_phone_numbers`, payload);
  return response.data?.data;
};

/**
 * Update business phone number (e.g., to update campaign settings)
 * @param {string} uid - The business phone number UID
 * @param {Object} data - The data to update
 * @returns {Promise<Object>} - The updated phone number data
 */
const updateBusinessPhoneNumber = async (uid, data) => {
  const response = await gatewayService.put(`${baseRoute}/business_phone_numbers/${uid}`, data);
  return response.data?.data;
};

export default {
  getCallsSettings,
  createCallsSettings,
  updateCallsSettings,
  getRemainingQuota,
  getBusinessActiveDedicatedNumber,
  claimNumber,
  getAvailablePhoneNumbers,
  createBusinessPhoneNumber,
  getBusinessPhoneNumbers,
  getAvailableVoiceSmsNumbers,
  claimPhoneNumberWithVerification,
  updateBusinessPhoneNumber,
};
